/**
 * Cloudflare Access login screen (PR #4d).
 *
 * The CORTEX SPA delegates authentication to Cloudflare Access
 * (Option B, PR #4b). Most of the time the user never sees this
 * screen — Cloudflare's own login page intercepts before the SPA
 * loads. They see this screen only when:
 *
 *   1. They were authenticated, their session expired, and the SPA's
 *      `/auth/me` call returned 401 — we render this so they have a
 *      visible "Sign in" button to re-trigger the Cloudflare flow.
 *
 *   2. They are running the SPA locally without a Cloudflare Access
 *      proxy in front of the API. In that case the API accepts a
 *      `X-Dev-Auth-Subject` header (`MODE=development` only) and this
 *      screen offers a picker for known-bootstrapped CF subjects so
 *      the developer can "sign in as" one of them.
 *
 *   3. `/auth/me` returned a non-401 error (network down, 500, etc.).
 *      We surface the error and offer Retry.
 */

import React, { useState } from 'react';
import { useToast } from '../ui/Toast';
import { HumanRisksLogo } from '../ui/HumanRisksLogo';
import { useAuth } from '../../auth';
import { setDevAuthSubject } from '../../services/cortexApi';

interface DevSubjectPreset {
  readonly subject: string;
  readonly label: string;
  readonly description: string;
}

/**
 * Known CF Access subjects that the `db:bootstrap-admin` script can
 * be invoked with. These are convenience presets ONLY — the developer
 * can also paste an arbitrary subject in the free-text field.
 *
 * Update this list (and re-run `db:bootstrap-admin` with the matching
 * `BOOTSTRAP_CF_SUBJECT`) as your local fixture data evolves.
 */
const DEV_SUBJECT_PRESETS: readonly DevSubjectPreset[] = [
  {
    subject: 'dev-admin@lombardinsurance.co.za',
    label: 'Lombard AdminSuperUser',
    description: 'Insurer · all permissions (use the bootstrap-admin default)',
  },
  {
    subject: 'dev-senior-uw@lombardinsurance.co.za',
    label: 'Lombard SeniorUnderwriter',
    description: 'Insurer · senior underwriter role',
  },
  {
    subject: 'dev-broker@acmebrokers.co.za',
    label: 'Acme SeniorBroker',
    description: 'Brokerage · facility-overlay required',
  },
];

export const CloudflareLoginScreen: React.FC = () => {
  const { state, refresh } = useAuth();
  const { showToast } = useToast();
  const [customSubject, setCustomSubject] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const errorMessage = state.status === 'error' ? state.message : null;

  const handleSignInToCloudflare = (): void => {
    // The CF Access app sits in front of the SPA's own origin in
    // production. Visiting any protected URL with no valid session
    // triggers CF's login redirect.
    setIsWorking(true);
    window.location.assign(window.location.origin);
  };

  const handleDevSignIn = async (subject: string): Promise<void> => {
    if (subject.trim().length === 0) {
      showToast('Pick a preset or paste a CF Access subject to sign in.', 'error');
      return;
    }
    setIsWorking(true);
    setDevAuthSubject(subject.trim());
    try {
      await refresh();
    } catch {
      showToast('Sign-in failed. Check the API is running and the subject exists.', 'error');
    } finally {
      setIsWorking(false);
    }
  };

  const handleRetry = async (): Promise<void> => {
    setIsWorking(true);
    try {
      await refresh();
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-hr-grey font-sans selection:bg-hr-pink selection:text-white">
      <div className="hidden lg:flex w-1/2 bg-hr-indigo relative overflow-hidden flex-col justify-between p-16">
        <div className="relative z-10">
          <HumanRisksLogo variant="white" className="h-10 mb-12" />
          <div className="space-y-6">
            <h1 className="text-5xl font-black text-white leading-[0.9] tracking-tight">
              CORTEX
              <span className="text-lg font-light text-hr-pink tracking-tight block mt-2">
                by Human Risks
              </span>
            </h1>
            <p className="text-slate-300 text-sm max-w-md font-light leading-relaxed">
              The brain behind the risk.
            </p>
          </div>
        </div>

        <div className="relative z-10 bg-white/5 border border-white/10 p-6 rounded-2xl max-w-xs mt-12 shadow-2xl">
          <div className="text-micro font-bold text-hr-pink uppercase tracking-widest mb-2">
            Identity Provider
          </div>
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 bg-hr-lime rounded-full" />
            <span className="text-white font-mono text-xs tracking-wide">CLOUDFLARE ACCESS</span>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-micro text-slate-400">
            Login, MFA, and password storage are handled by Cloudflare Access. CORTEX never sees
            your credentials. Internal users will move to Microsoft Entra ID SSO in v2 without
            changing the SPA.
          </div>
        </div>

        <div className="z-10 text-micro text-slate-500 font-bold uppercase tracking-widest mt-auto">
          © {new Date().getFullYear()} CORTEX by Human Risks (a specialist division of Lombard
          Insurance Company Limited)
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white relative">
        <div className="w-full max-w-[440px] space-y-8">
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-black text-hr-indigo tracking-tight">Welcome Back</h2>
            <p className="text-slate-500 mt-2 text-xs font-medium">
              Authenticate via Cloudflare Access to enter the underwriting console.
            </p>
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r text-rose-900 text-xs leading-relaxed"
            >
              <p className="font-bold uppercase tracking-wider text-rose-700 mb-1">
                Sign-in failed
              </p>
              <p>{errorMessage}</p>
              <button
                type="button"
                onClick={() => void handleRetry()}
                disabled={isWorking}
                className="mt-3 font-bold underline decoration-2 underline-offset-4 hover:text-rose-700 disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleSignInToCloudflare}
            disabled={isWorking}
            className="w-full bg-hr-pink hover:bg-hr-pink-hover text-white font-bold py-3.5 px-4 rounded-xl shadow-lg transition-colors text-xs uppercase tracking-wider flex justify-center items-center gap-2 disabled:opacity-50"
          >
            {isWorking ? 'Redirecting…' : 'Sign in with Cloudflare Access'}
          </button>

          {/* Dev-bypass picker — local `vite` dev only. Vite statically
              replaces `import.meta.env.DEV` with `false` in production
              builds, so this whole block is tree-shaken out of the
              deployed bundle (the API rejects the header in production
              regardless; this also removes the confusing buttons from the
              live login screen). */}
          {import.meta.env.DEV && (
            <div className="pt-6 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-amber-100 border border-amber-200 rounded text-micro font-black uppercase tracking-widest text-amber-700">
                  Dev only
                </span>
                <p className="text-xs font-bold text-slate-600">Local dev-bypass sign-in</p>
              </div>
              <p className="text-micro text-slate-400 mb-4 leading-relaxed">
                When the API is running locally (no Cloudflare Access proxy), pick a bootstrapped CF
                Access subject below. The API rejects this header in production.
              </p>

              <div className="space-y-2">
                {DEV_SUBJECT_PRESETS.map((preset) => (
                  <button
                    key={preset.subject}
                    type="button"
                    onClick={() => void handleDevSignIn(preset.subject)}
                    disabled={isWorking}
                    className="w-full text-left px-3 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors disabled:opacity-50 group"
                  >
                    <p className="text-xs font-bold text-slate-800 group-hover:text-hr-indigo">
                      {preset.label}
                    </p>
                    <p className="text-micro text-slate-500 font-medium">{preset.description}</p>
                    <p className="text-micro text-slate-400 font-mono mt-1 truncate">
                      {preset.subject}
                    </p>
                  </button>
                ))}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleDevSignIn(customSubject);
                }}
                className="mt-4"
              >
                <label
                  htmlFor="dev-subject-input"
                  className="block text-micro font-bold text-slate-600 uppercase tracking-wider mb-1.5"
                >
                  Or paste a CF Access subject
                </label>
                <div className="flex gap-2">
                  <input
                    id="dev-subject-input"
                    type="text"
                    className="flex-1 p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono focus:ring-2 focus:ring-hr-pink focus:border-hr-pink outline-none transition-colors"
                    placeholder="sub-claim@example.com"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    disabled={isWorking}
                  />
                  <button
                    type="submit"
                    disabled={isWorking || customSubject.trim().length === 0}
                    className="px-4 py-2.5 bg-hr-indigo hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider rounded-lg transition-colors disabled:opacity-50"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
